import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin } from 'lucide-react';
import PropertyCard from '@/components/PropertyCard';
import { useLocationCtx } from '@/contexts/LocationContext';
import { LocationSelector } from '@/components/LocationSelector';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const linkFor = (key, city) => {
  const c = city ? `&city=${encodeURIComponent(city)}` : '';
  if (key === 'rent_in_city') return `/properties?listing_type=rent${c}`;
  if (key === 'in_city' || key === 'near_you') return `/properties?${c.slice(1)}`;
  return '/properties?sort=newest';
};

export const LocalPropertySections = () => {
  const { location, status } = useLocationCtx();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (status === 'prompting' || status === 'detecting') return;
    setLoading(true);
    const p = new URLSearchParams({ limit: '8' });
    if (location?.city) p.set('city', location.city);
    if (location?.locality) p.set('locality', location.locality);
    fetch(`${API}/recommendations?${p}`).then((r) => r.json()).then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  }, [location?.city, location?.locality, status]);

  if (loading && !data) {
    return (
      <section className="py-14" data-testid="local-sections-loading">
        <div className="container-luxury grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-72 rounded-sm bg-slate-100 animate-pulse" />)}
        </div>
      </section>
    );
  }
  if (!data) return null;

  return (
    <div data-testid="local-sections">
      {data.city && !data.city_has_listings && (
        <section className="py-10 bg-[#FBF7EC]">
          <div className="container-luxury flex flex-col sm:flex-row sm:items-center gap-3 justify-between" data-testid="local-no-listings">
            <p className="text-sm text-[#0F172A]"><MapPin className="inline h-4 w-4 text-[#D4AF37] mr-1" />We don't have listings in <b>{data.city}</b> yet — showing nearby cities and featured homes. </p>
            <LocationSelector />
          </div>
        </section>
      )}
      {data.sections.map((s, idx) => (
        <section key={s.key} className={`py-14 ${idx % 2 ? 'bg-slate-50' : 'bg-white'}`} data-testid={`section-${s.key}`}>
          <div className="container-luxury">
            <div className="flex items-end justify-between mb-8 gap-4">
              <div>
                {idx === 0 && data.city && <p className="text-[#D4AF37] text-xs font-semibold uppercase tracking-[0.2em] mb-2">Location-first picks</p>}
                <h2 className="font-['Plus_Jakarta_Sans'] text-2xl md:text-3xl font-bold text-[#0F172A]" data-testid={`section-title-${s.key}`}>{s.title}</h2>
              </div>
              <Link to={linkFor(s.key, data.city)} className="text-sm text-[#0F172A] hover:text-[#D4AF37] flex items-center gap-1 whitespace-nowrap" data-testid={`section-viewall-${s.key}`}>View all <ArrowRight className="h-4 w-4" /></Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {s.items.slice(0, 8).map((p) => <PropertyCard key={p.property_id} property={p} />)}
            </div>
          </div>
        </section>
      ))}
    </div>
  );
};
